/* fswond — drawer base
 * Uso: <fs-drawer name="cart">…</fs-drawer>
 * Apri: window.FsEvents.emit('drawer:open', { name: 'cart' });
 */
(function () {
  'use strict';

  window.FsEvents = window.FsEvents || {
  _listeners: new Map(),
  on(event, fn) {
    if (!this._listeners.has(event)) this._listeners.set(event, new Set());
    this._listeners.get(event).add(fn);
    return () => this._listeners.get(event).delete(fn);
  },
  emit(event, detail) {
    (this._listeners.get(event) || []).forEach((fn) => {
      try { fn(detail); } catch (err) { console.error('[fs-events]', err); }
    });
  },
};

const findFsDrawer = (name) => {
  return Array.from(document.querySelectorAll('fs-drawer')).find((drawer) => drawer.getAttribute('name') === name);
};

class FsDrawer extends HTMLElement {
  connectedCallback() {
    if (this.dataset.drawerReady === 'true') return;
    this.dataset.drawerReady = 'true';
    this.name = this.getAttribute('name');
    this._onKeydown = this._onKeydown.bind(this);
    this._onBackdropClick = this._onBackdropClick.bind(this);
    this._onCloseClick = this._onCloseClick.bind(this);

    this._backdrop = this.querySelector('.fs-drawer__backdrop');
    this._closeBtns = this.querySelectorAll('[data-drawer-close]');

    if (this._backdrop) this._backdrop.addEventListener('click', this._onBackdropClick);
    this._closeBtns.forEach((b) => b.addEventListener('click', this._onCloseClick));

    if (window.FsEvents) {
      this._unsubOpen = window.FsEvents.on('drawer:open', (detail = {}) => {
        const { name } = detail;
        if (name === this.name) this.open();
      });
      this._unsubClose = window.FsEvents.on('drawer:close', (detail = {}) => {
        const { name } = detail;
        if (!name || name === this.name) this.close();
      });
    }
  }

  disconnectedCallback() {
    if (this._unsubOpen) this._unsubOpen();
    if (this._unsubClose) this._unsubClose();
    document.removeEventListener('keydown', this._onKeydown);
  }

  open() {
    this._previousActiveElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    // Close any other open drawer
    document.querySelectorAll('fs-drawer.is-open').forEach((d) => { if (d !== this) d.close(); });
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('fs-no-scroll');
    document.addEventListener('keydown', this._onKeydown);

    // focus trap entry
    const firstFocus = this.querySelector('[data-autofocus], button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    window.requestAnimationFrame(() => {
      if (firstFocus) firstFocus.focus({ preventScroll: true });
    });
    this.dispatchEvent(new CustomEvent('fs-drawer:opened', { bubbles: true }));
  }

  close() {
    const wasOpen = this.classList.contains('is-open');
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    if (!document.querySelector('fs-drawer.is-open')) {
      document.documentElement.classList.remove('fs-no-scroll');
    }
    document.removeEventListener('keydown', this._onKeydown);

    if (wasOpen && this._previousActiveElement && document.contains(this._previousActiveElement)) {
      this._previousActiveElement.focus({ preventScroll: true });
    }
    this.dispatchEvent(new CustomEvent('fs-drawer:closed', { bubbles: true }));
  }

  _onKeydown(e) {
    if (e.key === 'Escape') { e.preventDefault(); this.close(); }
    if (e.key === 'Tab') this._trapFocus(e);
  }

  _onBackdropClick() { this.close(); }
  _onCloseClick(e) { e.preventDefault(); this.close(); }

  _trapFocus(e) {
    const focusables = this.querySelectorAll('button, [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])');
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }
}

if (!customElements.get('fs-drawer')) customElements.define('fs-drawer', FsDrawer);

if (!window.FsDrawerTriggersReady) {
  window.FsDrawerTriggersReady = true;
  document.addEventListener('click', (event) => {
    if (event.defaultPrevented) return;
    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    const trigger = target?.closest('[data-drawer-trigger]');
    if (!trigger) return;

    const name = trigger.dataset.drawerTrigger;
    const drawer = findFsDrawer(name);
    if (!drawer) return;

    event.preventDefault();
    if (typeof drawer.open === 'function') {
      drawer.open();
    } else {
      window.FsEvents.emit('drawer:open', { name });
    }
  });
}

})();
